/* Fôring: gikk maskinen, og når stoppet den.
 *
 * BYGGET SOM EN STATUSSIDE, MED NEURATHS ENHET
 *   Spørsmålet er det samme som en driftsstatusside svarer på -- noe som skal
 *   gå: gikk det, og når brakk det -- og den formen forstår alle uten
 *   opplæring. Rekkefølgen er derfor deres: en DOM i ord øverst, så avbruddene
 *   som TEKST med klokkeslett, så én rad per døgn.
 *
 *   Avbruddet var før noe man måtte oppdage i en graf. En statusside gjetter
 *   ikke: den skriver «Partial outage, 08:26-08:50, 24 min». Det gjør denne nå.
 *
 *   Og enheten er Neuraths: for å vise dobbelt så mye, bruk to symboler, aldri
 *   ett dobbelt så stort. Elleve merker er elleve timer, og et avbrudd er et
 *   hull i rekken -- noe et menneske ser uten forklaring og uten farge.
 *
 * HVA SOM BLE SLETTET, OG HVORFOR
 *   Nivåkurva: 1440 små svingninger som ingen operatør gjør noe med. Arntz
 *   tegnet ikke skygger.
 *   Ringen: riktig for natten, som ER én avgrenset størrelse; feil for et døgn
 *   som har en form.
 *   Nattfeltet: målt rho = -0,02 over 17 hele døgn. Ingen sammenheng å vise,
 *   og en figur som antyder en er verre enn ingen figur.
 *
 * RØDT ER IKKE TILGJENGELIG
 *   Manualen har rødt til tre ting -- levendeindikatoren, en
 *   attribusjonskonflikt, de varmeste spektrogrambinsene -- og et fôringsstopp
 *   er ikke ett av dem. Dommen bæres derfor av ORD og av plassering, som
 *   dessuten virker for den som ikke skiller fargene. */

import { useMemo, useState } from 'react'

import { Alive, Rows } from '../components/bits'
import { PageHead } from '../components/Shell'
import { Baat, Dag, DagRad, Dager, Okt, Stopp, Vindu, klokke, kortDato }
  from '../components/Dogn'
import { useApi } from '../useApi'

interface SiteDay {
  period: string; hours: number | null; grade: string; n_sensors: number
  operations?: boolean; n_vessel_hours?: number; incomplete?: boolean
  n_stops: number; longest_stop_min: number | null; partial?: boolean
  first_on_utc: number | null; last_on_utc: number | null
  sessions: Okt[]; stops: Stopp[]; hours_state?: number[]
  /** Fôringsvinduet, beholdt når `hours` er byttet til blåsetid. */
  window_hours?: number | null
  /** Minutter blåsetid per time, 24 lange, null der 1 s-sporet mangler. */
  hours_minutes?: (number | null)[]
  /** Om timen lot seg måle. false = for lite spenn i båndet, ikke «null». */
  hours_resolved?: boolean[]
  /** Døgnets blåsetid i minutter. `hours` ved siden er fôringsVINDUET. */
  minutes_blowing?: number | null
  vessel_hours?: boolean[]; n_blind_hours?: number
  median_excess_db: number | null; grain: string | null; mission_id: string
}
interface FeedingDay { period: string; assumptions: string[] }
interface Feeding {
  schema: 1; site_id: string; analysed: boolean; reason?: string
  days: FeedingDay[]; site: SiteDay[]; timezone?: string
  normal?: { start_h: number | null; end_h: number | null
             sunrise_h: number | null; sunset_h: number | null
             from_h: number; to_h: number }
}
interface DaySensor { mission_id: string; on_seconds?: number[] }
interface DayMinutes {
  schema: 1; day: string; analysed: boolean; near_m: number; vessels: Baat[]
  sensors?: DaySensor[]
}

const nb = (n: number, d = 1) =>
  n.toLocaleString('nb-NO', { minimumFractionDigits: d, maximumFractionDigits: d })

/** Varighet som timer og minutter, aldri som desimaltimer.
 *
 *  «1,4 t» er en enhet man må regne om for å bruke: ingen operatør vet hva 0,4
 *  time er uten å tenke, og siden står allerede full av klokkeslett den skal
 *  leses sammen med. «1 t 24 min» kan sammenlignes med 07:00-08:24 direkte. */
const varighet = (t: number): string => {
  const m = Math.round(t * 60)
  const h = Math.floor(m / 60), r = m % 60
  if (h && r) return `${h} t ${r} min`
  return h ? `${h} t` : `${r} min`
}

const langDato = (p: string) =>
  new Date(`${p}T12:00:00Z`).toLocaleDateString('nb-NO',
    { weekday: 'long', day: 'numeric', month: 'long' })

const isoDag = (tilbake = 0) => {
  const d = new Date()
  d.setDate(d.getDate() - tilbake)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-`
    + `${String(d.getDate()).padStart(2, '0')}`
}
const iDagIso = () => isoDag(0)

/** «I dag» og «I går» framfor datoen, når det er en av dem.
 *
 *  Den som åpner siden om morgenen skal vite om stripen gjelder nå eller i
 *  går, og «mandag 24. august» er en omvei til det svaret. Datoen blir stående
 *  ved siden av, fordi den trengs i det øyeblikket man har bladd bakover. */
const dagsNavn = (p: string): string | null =>
  p === isoDag(0) ? 'I dag' : p === isoDag(1) ? 'I går' : null

/** Time på døgnet i GÅRDENS sone, som desimaltall.
 *
 *  Aldri leserens sone og aldri serverens: en operatør i en annen tidssone
 *  skal se gårdens klokke, ellers flytter fôringen seg uten at noe skjedde. */
function hod(v: number, tz?: string): number {
  const del = new Intl.DateTimeFormat('nb-NO', {
    timeZone: tz || undefined, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(v * 1000))
  const g = (k: string) => Number(del.find((x) => x.type === k)?.value ?? 0)
  return (g('hour') % 24) + g('minute') / 60
}

/** Døgn som kan stilles opp mot andre døgn.
 *
 *  Et døgn som pågår er ikke en kort dag, og et døgn med brønnbåt ved merdene
 *  er ikke en måling av fôring. Begge holdes utenfor hver gang et tall her
 *  sammenlignes med et annet. */
const hele = (xs: SiteDay[]) =>
  xs.filter((d) => !d.partial && !d.operations && !d.incomplete)

/** Blåsetimer FØR klokkeslettet H, summert fra døgnets egne timetall.
 *
 *  Timen klokka står i tas forholdsmessig. Det er en tilnærming inne i én time
 *  og eksakt for alle de andre; å hoppe over den ville underrapportert
 *  «vanlig» med inntil en time, hver formiddag, systematisk. */
function blaastInnen(d: SiteDay, H: number): number | null {
  const hm = d.hours_minutes
  if (!hm || hm.length < 24) return null
  let t = 0
  for (let h = 0; h < 24; h++) {
    const v = hm[h]
    if (v == null) continue
    if (h + 1 <= H) t += v
    else if (h < H) t += v * (H - h)
  }
  return t / 60
}

/** Fôrede timer FØR klokkeslettet H, regnet i gårdens sone.
 *
 *  Øktene bærer sine egne epoker, så dette trenger verken døgnets startepoke
 *  eller tidssonematte utover den ene omregningen -- og det gir brøkdeler av
 *  en time, slik at begge sider av sammenligningen har samme oppløsning. */
function foretTimerInnen(d: SiteDay, H: number, tz?: string): number {
  let t = 0
  for (const o of d.sessions ?? []) {
    const a = hod(o.start, tz), b = hod(o.end, tz)
    if (b <= a) continue          // økt over midnatt; fôring gjør ikke det
    t += Math.max(0, Math.min(H, b) - a)
  }
  return t
}

/** Dommen, i ord. Det første på siden, slik en statusside gjør det.
 *
 *  Rekkefølgen er tilsiktet: et avbrudd slår alt annet, fordi det er det
 *  eneste her en operatør kan gjøre noe med. */
/** Hvor langt døgnet er målt, i hele timer. Alt etter dette har ikke skjedd. */
function maaltTil(d: SiteDay): number {
  const h = d.hours_state ?? []
  let k = h.length
  while (k > 0 && h[k - 1] === 3) k--
  return k
}

/** Timer med fôring i de FØRSTE k timene av døgnet.
 *
 *  Det er dette som gjør et pågående døgn sammenlignbart: klokka ti er ikke
 *  dagens fôring kortere enn vanlig, den er bare ikke ferdig. Sammenligningen
 *  må gjelde de samme timene på begge sider. */
function foretInnen(d: SiteDay, k: number): number {
  return (d.hours_state ?? []).slice(0, k).filter((x) => x === 1 || x === 2).length
}

function dom(d: SiteDay | null, vanlig: number, ferdige: SiteDay[]):
  { ord: string; sterk: boolean } {
  if (!d) return { ord: 'Ingen måling', sterk: false }

  /* BÅT VED MERDENE OVERSTYRER IKKE LENGER, og det var en målefeil å la den
     gjøre det. Antagelsen var at en arbeidsbåt drukner fôringen i det samme
     båndet. Målt over 143 nattetimer løfter en fortøyd båt innenfor 400 m
     båndet 3,3 dB -- MINDRE enn de 4,2 dB en time uten båt i det hele tatt --
     og ingen av delene er i nærheten av terskelen på 18 dB. Regelen nektet å
     svare på tre gode døgn for å redde ett dårlig, og operatøren kunne høre
     fôringen tydelig i klippet mens siden meldte at den ikke kunne måles.
     Timene med båt er skravert i figuren i stedet: leseren ser dem, og
     målingen står. */

  /* Arbeid på anlegget står FØRST, men tallet blir stående. Med en brønnbåt
     som pumper fisk og en spylerigg på nota er båndet fullt av maskineri som
     måler seg med fôringen, og 18.-20. august leste detektoren 11-13 timer --
     MER enn en vanlig dag -- på dager gården fôret langt mindre.
     Maskeringen blåser opp; den tier ikke.
     Men de kan godt ha fôret deler av dagen: en brønnbåt ved merdene stopper
     ikke automaten. Så målingen kjører og vises, den er merket usikker, og
     dagen holdes utenfor alle grunnlinjer -- vanlig, normalvindu, endring. */
  if (d.operations) return { ord: 'Arbeid på anlegget', sterk: false }
  /* Målt, men ikke nok av døgnet til å stå som et døgn. Vises med det som
     ble funnet, aldri som en dagssum. */
  if (d.incomplete) return { ord: 'Delvis målt', sterk: false }
  if (d.n_stops > 0) {
    return { ord: d.n_stops === 1 ? 'Avbrudd i fôringen'
      : `${d.n_stops} avbrudd i fôringen`, sterk: true }
  }
  if (d.hours == null || d.hours <= 0) {
    return { ord: d.partial ? 'Ingen fôring målt ennå' : 'Ingen fôring målt',
             sterk: false }
  }

  /* Et døgn som pågår måles mot de SAMME timene på ferdige døgn, aldri mot et
     helt døgn. Uten det melder siden «kortere enn vanlig» hver formiddag, om
     en maskin som går helt som den skal. */
  if (d.partial) {
    const k = maaltTil(d)
    const mot = ferdige.filter((x) => !x.operations)
      .map((x) => foretInnen(x, k)).sort((a, b) => a - b)
    const norm = mot.length ? mot[Math.floor(mot.length / 2)]! : null
    if (norm != null && foretInnen(d, k) < norm - 1) {
      return { ord: 'Mindre enn vanlig så langt', sterk: true }
    }
    return { ord: 'Fôring som vanlig så langt', sterk: false }
  }

  if (vanlig > 0 && d.hours! < vanlig - 0.5) return { ord: 'Kortere enn vanlig', sterk: true }
  if (vanlig > 0 && d.hours! > vanlig + 0.75) return { ord: 'Lengre enn vanlig', sterk: false }
  return { ord: 'Fôret som vanlig', sterk: false }
}

/** Medianen av ferdige døgn. Uferdige holdes utenfor overalt der ett døgn
 *  sammenlignes med et annet -- et døgn målt til 12:00 er ikke en kort dag. */
function median(xs: number[]): number | null {
  const t = xs.filter((x) => x > 0).sort((a, b) => a - b)
  return t.length ? t[Math.floor(t.length / 2)]! : null
}

/** Endring mot forrige like lange periode.
 *
 *  ANALYTICS-SPØRSMÅLET, MED TO FORSKJELLER
 *    Google Analytics setter opp og ned i grønt og rødt. Manualen her har rødt
 *    til tre ting -- levendeindikatoren, en attribusjonskonflikt, de varmeste
 *    spektrogrambinsene -- og «fôret 20 minutter kortere» er ikke ett av dem.
 *    Retningen bæres av fortegnet og av pila, som dessuten virker for den som
 *    ikke skiller fargene.
 *
 *    Og ingenting her er «bra» eller «dårlig». Kortere fôring kan være en
 *    maskin som stoppet eller en operatør som skrudde ned med lavere appetitt,
 *    og siden kan ikke skille dem. Den melder forskjellen og lar operatøren
 *    vite hvilken det var.
 *
 *  UFERDIGE DØGN TELLER IKKE. Dagen i dag er halvmålt til lunsj, og å la den
 *  inn ville meldt at fôringen faller hver formiddag og tar seg opp hver kveld. */
function Endring({ na, for: fr, vindu, tz }: {
  na: SiteDay[]; for: SiteDay[]; vindu: number; tz?: string
}) {
  const tall = (xs: SiteDay[]) =>
    xs.map((d) => d.hours).filter((h): h is number => !!h && h > 0)
  const t = median(tall(hele(na)))
  const tF = median(tall(hele(fr)))
  const st = hele(na).reduce((a, d) => a + d.n_stops, 0)
  const stF = hele(fr).reduce((a, d) => a + d.n_stops, 0)

  const timer = (xs: SiteDay[]) => median(
    hele(xs).map((d) => d.first_on_utc).filter((v): v is number => v != null)
      .map((v) => hod(v, tz)))
  const s0 = timer(na), s0F = timer(fr)

  if (t == null) return null
  const felt: { navn: string; verdi: string; d: number | null; enhet: string }[] = [
    /* Navnene sier hva slags tall det er, fordi de ikke er samme slags tall:
       to medianer og én sum ved siden av hverandre leses som tre av det samme.
       «1 avbrudd» over en uke er riktig og så det ut som en dag, mens «9 t 36
       min» over en dag så ut som en uke. */
    { navn: 'fôret per døgn', verdi: varighet(t),
      d: tF == null ? null : t - tF, enhet: 't' },
    { navn: 'avbrudd i perioden', verdi: `${st}`,
      d: fr.length ? st - stF : null, enhet: '' },
    { navn: 'start per døgn', verdi: s0 == null ? '–' : klokkeslett(s0),
      d: s0 == null || s0F == null ? null : (s0 - s0F) * 60, enhet: 'min' },
  ]

  return (
    <section className="endring" aria-label={`Endring mot forrige ${vindu} døgn`}>
      {felt.map((x) => (
        <div key={x.navn} className="endring-felt">
          <span className="endring-verdi">{x.verdi}</span>
          <span className="endring-navn">{x.navn}</span>
          <Delta d={x.d} enhet={x.enhet} />
        </div>
      ))}
    </section>
  )
}

function Delta({ d, enhet }: { d: number | null; enhet: string }) {
  if (d == null) return <span className="endring-d tom">ingen sammenligning</span>
  const rundet = enhet === 'min' ? Math.round(d)
    : enhet === 't' ? Math.round(d * 10) / 10 : d
  if (!rundet) return <span className="endring-d flat">uendret</span>
  const tall = enhet === 't' ? nb(Math.abs(rundet)) : `${Math.abs(rundet)}`
  return (
    <span className={`endring-d ${rundet > 0 ? 'opp' : 'ned'}`}>
      <span aria-hidden="true">{rundet > 0 ? '▲' : '▼'}</span>
      {' '}{tall}{enhet && ` ${enhet}`}
    </span>
  )
}

const klokkeslett = (h: number) =>
  `${String(Math.floor(h)).padStart(2, '0')}:`
  + `${String(Math.round((h % 1) * 60)).padStart(2, '0')}`

export function SiteFeeding({ id }: { id: string }) {
  /* Sju døgn, fast. 7/14/30-velgeren er borte: den og pilene svarte på det
     samme spørsmålet -- «vis meg en annen periode» -- og med to måter å gjøre
     det på måtte leseren først finne ut hvilken av dem som gjaldt. Pilene
     vinner fordi de går like langt bakover uten å endre hva en rad betyr. */
  const vindu = 7
  const [valgt, setValgt] = useState<string | null>(null)
  /* Hvor mange vinduer bakover vi står. 0 er nå. Siden `until` er eksklusiv
     slutt, er det bare å trekke fra vindu * side dager. */
  const [side, setSide] = useState(0)

  const sted = useApi<{ name: string }>(`/v1/sites/${encodeURIComponent(id)}`)
  /* Doble vinduet og hent ETT kall: den nyeste halvparten er perioden, den
     eldste er sammenligningen. En rad er noen hundre byte, så seksti døgn er
     titalls kilobyte -- billigere enn to kall, og de kan ikke komme i utakt. */
  const til = useMemo(() => {
    if (!side) return null
    const d = new Date()
    d.setDate(d.getDate() - vindu * side)
    return d.toISOString()
  }, [side, vindu])
  /* Denne siden leses av noen som står på merdkanten mens automaten går, og
     dagen som pågår er den eneste raden de er ute etter. Uten polling svarer
     siden med det som var sant da fanen ble åpnet: en fane åpnet før første
     fôringsminutt ble foldet, blir stående på «Ingen fôring målt ennå» hele
     formiddagen mens automaten går -- observert 8. sep. 2026 kl. 07:38, da
     API-et samtidig svarte 0,9 timer. Analysen folder omtrent hvert 20. minutt,
     så fem minutter er rikelig og billig. Samme intervall som Kart.tsx. */
  const f = useApi<Feeding>(
    `/v1/sites/${encodeURIComponent(id)}/feeding?days=${vindu * 2}`
    + (til ? `&until=${encodeURIComponent(til)}` : ''),
    { poll: 300_000 })

  /* HVA SIDEN RAPPORTERER, BESTEMT ÉN GANG.
     Detektoren måler nå blåsetid -- sekundene maskinen faktisk gikk -- og det
     er et annet tall enn fôringsvinduet siden viste før: omtrent halvparten,
     ved et forhold som flytter seg med maskinens rate og derfor ikke kan
     regnes om etterpå. Byttet gjøres HER, på veien inn, så median, endring,
     dom og radene alle regner på det samme.

     Alt eller ingenting, og det er hele poenget med flagget: en median av noen
     døgn målt som vindu og noen som blåsetid er ikke et tall om noe som helst.
     Mangler ett eneste døgn blåsetid, viser siden vinduet slik den alltid har
     gjort. Vinduet blir uansett stående som `window_hours`. */
  const alle = useMemo(() => {
    const raw = [...(f.data?.site ?? [])]
      .sort((a, b) => a.period.localeCompare(b.period))
    const bruk = raw.length > 0 && raw.every((d) => d.minutes_blowing != null)
    return raw.map((d) => ({
      ...d, window_hours: d.hours,
      hours: bruk ? (d.minutes_blowing as number) / 60 : d.hours,
    }))
  }, [f.data])
  const viserBlaasetid = useMemo(
    () => alle.length > 0 && alle.every((d) => d.minutes_blowing != null),
    [alle])
  /* Perioden er den nyeste halvparten; resten finnes bare for å regne endring. */
  const site = useMemo(() => alle.slice(-vindu), [alle, vindu])
  const forrige = useMemo(() => alle.slice(0, Math.max(0, alle.length - vindu)),
                          [alle, vindu])

  /* «Vanlig» er medianen av gårdens egne målte døgn. Ikke gjennomsnittet: én
     dag med høytrykkspyling drar et snitt, medianen bryr seg ikke. */
  const vanlig = useMemo(() => {
    const t = site.filter((d) => !d.partial && !d.operations && !d.incomplete)
      .map((d) => d.hours).filter((h): h is number => !!h && h > 0)
      .sort((a, b) => a - b)
    return t.length ? t[Math.floor(t.length / 2)]! : 0
  }, [site])

  const dag = valgt ?? site[site.length - 1]?.period ?? null
  const rad = site.find((d) => d.period === dag) ?? null

  /* «Vanlig» må gjelde SAMME tid på døgnet.
     Et pågående døgn stilt opp mot medianen av hele døgn melder «1 t 24 min,
     vanlig 9 t 36 min» klokka halv ni -- en maskin som går akkurat som den
     skal, framstilt som et stort avvik. Snittet legges derfor ved klokka nå,
     på begge sider. Dommen over har regnet slik hele tiden; det var bare
     tallinja under den som ikke gjorde det. */
  const normalNaa = useMemo(() => {
    if (!rad?.partial) return null
    const tz = f.data?.timezone
    const naa = dag === iDagIso() ? hod(Date.now() / 1000, tz) : maaltTil(rad)
    // Blåsetid innen klokka nå kommer fra timetallene, som ALLEREDE er
    // blåsetid. Øktene måler vinduet, og å blande de to ville stilt dagens
    // blåsetid mot nabodagenes vindu -- feil vei, og stort nok til å melde
    // «mindre enn vanlig» hver formiddag om en maskin som går som den skal.
    const mot = hele(site)
      .map((x) => viserBlaasetid ? blaastInnen(x, naa) : foretTimerInnen(x, naa, tz))
      .filter((t): t is number => t != null && t > 0).sort((a, b) => a - b)
    return mot.length ? { timer: mot[Math.floor(mot.length / 2)]!, klokke: naa } : null
  }, [rad, site, dag, f.data, viserBlaasetid])

  /* Bare båtene hentes per dag nå. Øktene følger med hver rad i listen, så hele
     stabelen tegnes uten ett kall per rad -- og uten 1440 boolske verdier per
     døgn for nøyaktig det samme bildet. */
  const m = useApi<DayMinutes>(
    dag ? `/v1/sites/${encodeURIComponent(id)}/feeding/${dag}` : null,
    { poll: 300_000 })

  /* Kvartersminuttene til dagsstripa, foldet fra dagsendepunktets 1440
     sekundtall for DEN sensoren raden gjelder -- ikke et snitt over dem som
     var i vannet, som ville blandet en som hører fôringen med en som ikke gjør
     det.

     Timer uten oppløsning nulles ut igjen. Analysatoren sender null i
     `hours_minutes` der båndet er for sammentrykt til å finne en pause i, og
     der faller minuttallene tilbake på hele minutter. Foldet rått ville de
     fylt kvarteret helt, og «blåseren gikk hele kvarteret» og «vi kunne ikke
     se det» ville sett like ut -- den ene forvekslingen denne siden ikke får
     lov til å gjøre. */
  const kvarter = useMemo(() => {
    const sek = m.data?.sensors?.find((x) => x.mission_id === rad?.mission_id)
      ?.on_seconds
    if (!sek || sek.length < 1440) return undefined
    const ut: (number | null)[] = []
    for (let q = 0; q < 96; q++) {
      let sum = 0, sett = 0
      for (let i = q * 15; i < q * 15 + 15; i++) {
        if (sek[i]! >= 0) { sum += sek[i]!; sett++ }
      }
      ut.push(sett ? sum / 60 : null)
    }
    return ut
  }, [m.data, rad])

  /* Timens tvil, brettet ut på kvarterene den inneholder. */
  const kvarterUvisse = useMemo(() => {
    const hr = rad?.hours_resolved
    if (!hr || hr.length < 24) return undefined
    return Array.from({ length: 96 }, (_, q) => hr[q >> 2] !== false)
  }, [rad])

  if (f.cold || sted.cold) return <Rows n={6} />
  if (f.error) return <p className="tom">Kunne ikke hente fôringsdata.</p>
  if (f.data && !f.data.analysed) {
    return (
      <>
        <PageHead title={sted.data?.name ?? 'Fôring'} tight />
        <p className="tom">
          {f.data.reason === 'no_deployments_at_this_site'
            ? 'Ingen hydrofoner her ennå.' : 'Ingen analyse ennå.'}
        </p>
      </>
    )
  }

  const baater = m.data?.vessels ?? []
  const naer = baater.filter((b) => (b.closest_m ?? 1e9) <= 120)
  const d = dom(rad, vanlig, site.filter((x) => !x.partial))
  const i = site.findIndex((x) => x.period === dag)
  /* Ett felles vindu for alle rader. Uten det spente hver rad fra sin egen
     første til sin egen siste økt, og da så en dag som begynte en time seinere
     nøyaktig ut som en vanlig dag -- den ene forskjellen figuren fantes for. */
  const akse: Vindu = {
    fromH: f.data?.normal?.from_h ?? 5,
    toH: Math.max((f.data?.normal?.from_h ?? 5) + 4, f.data?.normal?.to_h ?? 18),
    startH: f.data?.normal?.start_h ?? null,
    endH: f.data?.normal?.end_h ?? null,
    sunriseH: f.data?.normal?.sunrise_h ?? null,
    sunsetH: f.data?.normal?.sunset_h ?? null,
  }
  /* Nyeste døgn ØVERST. Listen kom sortert eldst først, som er riktig for en
     akse og feil for en tabell: det man leser først skal være det som gjelder
     nå, ikke det som gjaldt for en uke siden. */
  const rader: DagRad[] = site.map((x) => ({
    period: x.period, okter: x.sessions ?? [], stopp: x.stops ?? [],
    timer: x.hours_state, baatTimer: x.vessel_hours,
    minutter: x.hours_minutes, opplost: x.hours_resolved,
    hours: x.hours, harBaat: (x.vessel_hours ?? []).some(Boolean),
    arbeid: !!x.operations || !!x.incomplete,
    paagaar: !!x.partial,
  })).reverse()

  const naerNavn = [...new Map(naer.map((b) => [b.name || 'ukjent', b])).values()]

  return (
    <>
      <PageHead title={sted.data?.name ?? 'Fôring'} tight />

      <div className="dag-topp">
        <span className="dag-dato">
          {dag ? dagsNavn(dag) ?? langDato(dag) : '–'}
        </span>
        {dag && dagsNavn(dag) && (
          <span className="dag-dato-full">{langDato(dag)}</span>
        )}
        {/* Pulsen er manualens levendeindikator, og et døgn som fortsatt måles
            er nøyaktig det tilfellet den finnes for: data kommer inn NÅ. Det
            er også den ene av manualens tre røde bruk som gjelder her. */}
        {rad?.partial && <span className="dag-naa live"><Alive label="Måler nå" />måler nå</span>}
        <div className="dag-nav">
          <button aria-label="forrige døgn" disabled={i <= 0}
                  onClick={() => setValgt(site[i - 1]!.period)}>‹</button>
          <button aria-label="neste døgn" disabled={i < 0 || i >= site.length - 1}
                  onClick={() => setValgt(site[i + 1]!.period)}>›</button>
        </div>
      </div>

      {/* Dommen. Alt annet på siden er belegg for denne linja. */}
      <h2 className={`dom${d.sterk ? ' sterk' : ''}`}>{d.ord}</h2>
      {/* Hull i opptaket, ikke «båt ved merdene». Den gamle teksten skyldte på
          en arbeidsbåt for timer hydrofonen rett og slett ikke hadde levert --
          og på et døgn som pågår var de tolv «umålbare» timene bare resten av
          dagen. Båt er nå skravur i figuren, der den hører hjemme. */}
      {(rad?.n_blind_hours ?? 0) >= 3 && (
        <p className="dom-blind">
          {rad!.n_blind_hours} timer uten opptak
        </p>
      )}
      <p className="dom-tall">
        {rad && rad.hours != null && rad.hours > 0
          ? <>{varighet(rad.hours)}{rad.partial && ' så langt'}
              {rad.first_on_utc && rad.last_on_utc
                ? <> · {klokke(rad.first_on_utc)}–{klokke(rad.last_on_utc)}</> : null}
              {!rad.operations && (rad.partial
                ? normalNaa && <> · vanlig innen {klokkeslett(normalNaa.klokke)}:
                    {' '}{varighet(normalNaa.timer)}</>
                : vanlig > 0 && <> · vanlig {varighet(vanlig)}</>)}
              {/* Vinduet blir stående. Tallet over er blåsetid og omtrent
                  halvparten av det siden viste før, og et fall fra ti timer
                  til fire uten noe ved siden av leses som en feil framfor som
                  en annen måling. */}
              {viserBlaasetid && rad.window_hours != null && rad.window_hours > 0
                && <> · i arbeid {varighet(rad.window_hours)}</>}
              {rad.operations && <> · usikkert, {rad.n_vessel_hours} timer med båt
                ved merdene</>}
              {rad.incomplete && !rad.operations && <> · delvis målt</>}</>
          : 'ingen fôring registrert'}
      </p>

      {/* Avbruddene som TEKST, med klokkeslett. Dette manglet: man skulle
          oppdage stoppet i en graf framfor å få det fortalt. */}
      {rad && (rad.stops?.length ?? 0) > 0 && (
        <ul className="avbrudd">
          {rad.stops.map((s) => (
            <li key={s.start}>
              <span className="avbrudd-tid">{klokke(s.start)}–{klokke(s.end)}</span>
              <span className="avbrudd-lengde">{Math.round(s.minutes)} min</span>
            </li>
          ))}
        </ul>
      )}

      {rad && <Dag okter={rad.sessions ?? []} timer={rad.hours_state}
                   stopp={rad.stops ?? []} baater={baater}
                   baatTimer={rad.vessel_hours} minutter={kvarter}
                   uvisse={kvarterUvisse}
                   erIDag={rad.period === iDagIso() && !!rad.partial}
                   vindu={akse} period={rad.period} />}

      {naerNavn.length > 0 && (
        <p className="baater">Båt ved anlegget:{' '}
          {naerNavn.map((b, k) => (
            <span key={b.name || k}>{k > 0 && ' · '}
              <b>{b.name || 'ukjent'}</b>{' '}
              {Math.round(Math.min(...naer
                .filter((x) => (x.name || 'ukjent') === (b.name || 'ukjent'))
                .map((x) => x.closest_m ?? 1e9)))} m</span>
          ))}
        </p>
      )}

      <section className="rytme">
        <div className="rytme-topp">
          <h3>Døgn</h3>
          {/* Perioden hører til pilene som bytter den, ikke til overskriften.
              Sto den til venstre leste den som en undertittel til «Døgn», og
              da gjaldt den tilsynelatende stripen øverst på siden også -- som
              er i dag, og ikke denne uka. */}
          {site.length > 0 && (
            <p className="rytme-spenn">
              {kortDato(site[0]!.period)}–{kortDato(site[site.length - 1]!.period)}
              {side > 0 && <> · {side * vindu} døgn tilbake</>}
              {/* Nevneren, fordi tallene under ikke gjelder alle sju døgnene.
                  Et døgn med arbeid på anlegget er ikke en fôringsmåling og
                  holdes utenfor -- men uten at det står her, leses «1 avbrudd»
                  som en uke med ett avbrudd, mens listen rett under viser
                  skravur på flere dager. Da er det tallet som får skylden. */}
              {hele(site).length < site.length && <>
                {' '}· {hele(site).length} av {site.length} døgn i tallene</>}
            </p>
          )}
          {/* Bla vindu for vindu bakover. Uten dette stopper siden ved de
              siste sju døgnene. */}
          <div className="dag-nav">
            <button aria-label={`forrige ${vindu} døgn`}
                    onClick={() => { setSide(side + 1); setValgt(null) }}>‹</button>
            <button aria-label={`neste ${vindu} døgn`} disabled={side === 0}
                    onClick={() => { setSide(Math.max(0, side - 1)); setValgt(null) }}>›</button>
          </div>
        </div>

        {/* Endring mot forrige like lange periode. */}
        <Endring na={site} for={forrige} vindu={vindu} tz={f.data?.timezone} />

        <Dager rader={rader} valgt={dag} velg={setValgt} vanlig={vanlig}
               vindu={akse} />
      </section>

      <Tegn assumptions={f.data?.days?.[0]?.assumptions ?? []} vanlig={vanlig}
            viserBlaasetid={viserBlaasetid} />
    </>
  )
}

function Tegn({ assumptions, vanlig, viserBlaasetid }: {
  assumptions: string[]; vanlig: number; viserBlaasetid: boolean
}) {
  return (
    <details className="tegn">
      <summary>Tegnforklaring</summary>
      <div className="tegn-inn">
        <dl>
          <dt>ett merke</dt>
          <dd>ett kvarter fôring øverst, én time i døgnlisten. Et avbrudd er et
              hull i rekken.</dd>
          <dt>lyst merke</dt>
          <dd>timen gikk minst 3 dB under resten av dagen — som når mengden
              skrus ned. Målt mot dagens eget nivå, ikke mot andre døgn.</dd>
          <dt>aksen</dt>
          <dd>samme timer for alle døgn, med hakk der gården pleier å begynne
              og slutte, så en sein start er et tomt merke til venstre.</dd>
          <dt>sporet</dt>
          <dd>strekker seg fra soloppgang til solnedgang — vinduet fôring kan
              skje i. Radens lengde er lyset, merkene er det som ble brukt av
              det. Regnet ut, ikke hentet.</dd>
          <dt>{viserBlaasetid ? 'blåsetid' : 'timer'}</dt>
          <dd>{viserBlaasetid
            ? 'sekundene blåseren faktisk gikk, summert. Maskinen pulser -- '
              + 'omkring ti sekunder på, tretten av -- så den er rundt '
              + 'halvparten av tida anlegget var i arbeid. «I arbeid» står ved '
              + 'siden av, og de to kan ikke regnes om til hverandre: '
              + 'forholdet flytter seg med maskinens rate.'
            : 'timene fôringen ble målt i.'}</dd>
          <dt>vanlig</dt>
          <dd>{vanlig ? `medianen av gårdens egne målte døgn, ${varighet(vanlig)}`
            + '. På et døgn som pågår er den skåret ved klokka nå, så begge '
            + 'sider gjelder de samme timene.'
            : 'for lite historikk ennå'}</dd>
          <dt>skravert fylling</dt>
          <dd>timen lot seg ikke måle: båndet hadde for lite spenn til at en
              pause mellom blåsene kunne finnes -- en arbeidsbåt langs merden,
              eller maskinen av. Høyden er det som ble målt, og skraveringen
              sier at tallet er svakt. Den tegnes aldri full: en time ingen
              kunne måle skal ikke se ut som dagens travleste.</dd>
          <dt>skravert merke</dt>
          <dd>båt nærmere enn 120 m den timen. Målingen skiller ikke en
              fôringsblåser fra en arbeidsbåt i det samme båndet, så timen er
              merket framfor å bli filtrert bort eller stilltiende talt med.</dd>
          <dt>natt mot dag</dt>
          <dd>ingen målbar sammenheng mellom støy om natten og fôringen dagen
              etter her ennå — 17 hele døgn, ni av dem med hvilescore 100,
              rho = −0,02.</dd>
        </dl>
        <h4>Om målingen</h4>
        <ul>{assumptions.map((a) => <li key={a}>{a}</li>)}</ul>
      </div>
    </details>
  )
}
